import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";
import { useGetSidemenu } from "../../features/settings/api/react-query";
import { getGeneralSetting } from "../../features/settings/api/settings";
import api from "../../shared/api/client";
import { Button } from "../../shared/ui/button";
import { InfoTile } from "../../shared/ui/info-tile";
import { AppDialog } from "../../shared/ui/composites";
import CustomizePopover from "./CustomizePopover";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "../../shared/ui/sidebar";
import { cn } from "../../lib/utils";
import { localizeDigits } from "../../shared/utils/numberLocale";
import { getPersianDateFromGregorian } from "../../shared/utils/persianDate";
import {
  canAccessReportDesigner,
  filterSidemenuItems,
} from "../../shared/permissions";

const IconMap = {
  home: "🏠",
  user: "👤",
  reports: "📊",
  setting: "⚙️",
  bell: "🔔",
};

const sidebarUserClasses =
  "mb-4 flex flex-col overflow-x-hidden rounded-[22px] border border-[rgba(99,102,241,0.14)] bg-user-card p-3.5 shadow-user-card";

function footerMicroButtonClass(collapsed) {
  return cn(
    "inline-flex h-5 shrink-0 items-center justify-center gap-0.5 rounded-md border border-slate-200 bg-white px-1.5 text-[10px] font-bold leading-none text-slate-600 hover:bg-slate-50",
    collapsed && "lg:h-4 lg:w-4 lg:gap-0 lg:px-0",
  );
}

function compactBuildDate(value) {
  const date = !value || value === "local" ? new Date() : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const { year, month, day } = getPersianDateFromGregorian(date);
  const raw = `${year}/${String(month).padStart(2, "0")}/${String(day).padStart(2, "0")}`;
  return localizeDigits(raw);
}

/** Lives outside the Sheet so it does not remount and immediately close the drawer. */
export function CloseMobileSidebarOnNavigate() {
  const location = useLocation();
  const { setOpenMobile } = useSidebar();

  useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname, setOpenMobile]);

  return null;
}

export default function AppSidebar() {
  const navigate = useNavigate();
  const { profile, loading, logout, can } = useAuth();
  const { data: sidemenu = [] } = useGetSidemenu(!!profile);
  const { isMobile, setOpenMobile, state, toggleSidebar } = useSidebar();
  const collapsed = !isMobile && state === "collapsed";
  const [appTitle, setAppTitle] = useState(
    () => process.env.REACT_APP_APP_NAME || "هدف یار",
  );
  const [appVersion, setAppVersion] = useState(null);
  const [buildMeta, setBuildMeta] = useState(() => ({
    commit: process.env.REACT_APP_BUILD_COMMIT || null,
    build_time: process.env.REACT_APP_BUILD_TIME || null,
  }));
  const [footerModal, setFooterModal] = useState(null);

  useEffect(() => {
    if (!profile) return;
    let cancelled = false;
    getGeneralSetting()
      .then((row) => {
        if (cancelled) return;
        const json = row?.settings_json || {};
        if (json.title) setAppTitle(String(json.title));
        if (json.version != null && json.version !== "") {
          setAppVersion(String(json.version));
        }
      })
      .catch(() => {});
    api
      .get("/version")
      .then((res) => {
        if (cancelled) return;
        setBuildMeta({
          commit:
            res.data?.commit && res.data.commit !== "dev"
              ? res.data.commit
              : process.env.REACT_APP_BUILD_COMMIT || res.data?.commit || null,
          build_time: res.data?.build_time || null,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [profile]);

  const displayName =
    profile?.full_name ||
    `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() ||
    "کاربر";
  const initials = displayName.slice(0, 1);

  const handleLogout = async () => {
    try {
      await logout();
      if (isMobile) setOpenMobile(false);
      navigate("/login");
    } catch (e) {
      console.error("Logout failed", e);
    }
  };

  const job = profile?.job_details;
  const showReportDesigner = canAccessReportDesigner(profile);
  const showAudit = typeof can === "function" && can("audit.view");

  // Navigation only: job_details.sidemenu. Route access uses permissions separately.
  const menuItems = filterSidemenuItems(job, sidemenu);

  return (
    <Sidebar
      side="right"
      collapsible="icon"
      className="border-l border-slate-200 bg-sidebar-gradient p-0 shadow-sidebar [&>div]:bg-sidebar-gradient"
    >
      <SidebarHeader className="p-4 pb-0 max-[1200px]:p-3.5 max-[1200px]:pb-0 max-[480px]:p-3 max-[480px]:pb-0">
        <div
          className={cn(
            "mb-3 flex min-h-12 items-start justify-start",
            collapsed && "lg:mb-2 lg:flex-col lg:justify-start",
          )}
        >
          <h2
            className={cn(
              "text-xl font-black text-slate-900",
              collapsed && "lg:hidden",
            )}
          >
            {appTitle}
          </h2>
          <Button
            type="button"
            variant="icon"
            size="close"
            className="hidden shrink-0 lg:inline-flex"
            onClick={toggleSidebar}
            aria-label={collapsed ? "باز کردن منو" : "جمع کردن منو"}
            aria-expanded={!collapsed}
            title={collapsed ? "باز کردن منو" : "جمع کردن منو"}
          >
            <span aria-hidden="true" className="text-lg leading-none">
              {collapsed ? "«" : "»"}
            </span>
          </Button>
          <Button
            type="button"
            variant="icon"
            size="close"
            className="hidden max-lg:inline-flex"
            onClick={() => setOpenMobile(false)}
            aria-label="بستن منو"
          >
            ×
          </Button>
        </div>
      </SidebarHeader>

      <SidebarContent className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 pb-4 max-[1200px]:px-3.5 max-[1200px]:pb-3.5 max-[480px]:px-3 max-[480px]:pb-3">
        {loading ? (
          <div
            className={cn(
              "flex items-center justify-center font-extrabold text-slate-500",
              collapsed && "lg:hidden",
            )}
          >
            در حال بارگذاری...
          </div>
        ) : (
          <nav className="flex flex-col gap-2 py-2">
            <div
              className={cn(
                sidebarUserClasses,
                collapsed && "lg:mb-2 lg:items-center lg:rounded-2xl p-3.5 ",
              )}
            >
              {profile ? (
                <div
                  className={cn(
                    "w-[221px] min-w-[221px] max-w-[221px]",
                    collapsed && "lg:flex lg:flex-col lg:items-center",
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center gap-3 border-b border-slate-400/25 pb-3",
                      collapsed && "lg:border-0 lg:justify-center lg:pb-0",
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-avatar-gradient font-black text-white shadow-user-avatar",
                        collapsed && "lg:h-10 lg:w-10 lg:rounded-xl",
                      )}
                      title={displayName}
                    >
                      {initials}
                    </div>
                    <div className={cn(collapsed && "lg:hidden")}>
                      <div className="font-black leading-snug text-slate-900">
                        {displayName}
                      </div>
                      <div className="text-sm leading-snug text-slate-500">
                        {profile.job_details?.name || "بدون نقش"}
                      </div>
                    </div>
                  </div>

                  <div
                    className={cn(
                      "mt-3 grid grid-cols-2 gap-2 max-lg:grid-cols-1",
                      collapsed && "lg:hidden",
                    )}
                  >
                    {[
                      { label: "کد پرسنلی", value: profile.personnel_code || "-" },
                      { label: "کد ملی", value: profile.national_code || "-" },
                      {
                        label: "مرکز",
                        value: profile.dc_name || "-",
                        className: "col-span-2 max-lg:col-span-1",
                      },
                    ].map((tile) => (
                      <InfoTile
                        key={tile.label}
                        label={tile.label}
                        value={tile.value}
                        className={tile.className}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className={cn("w-full text-center", collapsed && "lg:px-0")}>
                  <p
                    className={cn(
                      "mb-2 text-slate-500",
                      collapsed && "lg:hidden",
                    )}
                  >
                    حساب وارد نشده
                  </p>
                  <a
                    href="/login"
                    className="font-extrabold text-primary"
                    title="ورود"
                  >
                    ورود
                  </a>
                </div>
              )}
            </div>

            {menuItems.map((item) => (
              <NavLink
                key={item.key}
                to={item.route}
                end={item.route === "/"}
                onClick={() => {
                  if (isMobile) setOpenMobile(false);
                }}
                title={item.title}
                className={({ isActive }) =>
                  cn(
                    "flex min-h-11 w-full cursor-pointer items-center justify-start gap-2.5 rounded-card border border-slate-200 bg-white px-3.5 text-right text-base font-extrabold text-slate-600 no-underline shadow-nav-button transition-all duration-200 hover:-translate-x-1 hover:border-primary/25 hover:bg-slate-50 max-[1200px]:px-3",
                    collapsed &&
                      "lg:min-h-11 lg:justify-center lg:gap-0 lg:px-0 lg:hover:translate-x-0",
                    isActive &&
                      "border-primary/40 bg-nav-active text-sidebar-accent-foreground shadow-nav-button-active",
                  )
                }
              >
                {item.icon && (
                  <span className="nav-icon text-inherit" aria-hidden="true">
                    {IconMap[item.icon] || item.icon}
                  </span>
                )}
                <span
                  className={cn(
                    "nav-label text-inherit",
                    collapsed && "lg:hidden",
                  )}
                >
                  {item.title}
                </span>
              </NavLink>
            ))}
            {showAudit && (
              <NavLink
                to="/audit"
                onClick={() => { if (isMobile) setOpenMobile(false); }}
                title="گزارش رویدادها"
                className={({ isActive }) =>
                  cn(
                    "flex min-h-11 w-full cursor-pointer items-center justify-start gap-2.5 rounded-card border border-slate-200 bg-white px-3.5 text-right text-sm font-semibold text-slate-500 no-underline shadow-nav-button transition-all duration-200 hover:-translate-x-1 hover:border-primary/25 hover:bg-slate-50 max-[1200px]:px-3",
                    collapsed && "lg:min-h-11 lg:justify-center lg:gap-0 lg:px-0 lg:hover:translate-x-0",
                    isActive && "border-primary/40 bg-nav-active text-sidebar-accent-foreground shadow-nav-button-active",
                  )
                }
              >
                <span className="nav-icon text-inherit" aria-hidden="true">🧾</span>
                <span className={cn("nav-label text-inherit", collapsed && "lg:hidden")}>
                  گزارش رویدادها
                </span>
              </NavLink>
            )}
            {showReportDesigner && (
              <NavLink
                to="/reports/designer"
                onClick={() => { if (isMobile) setOpenMobile(false); }}
                title="طراح گزارش"
                className={({ isActive }) =>
                  cn(
                    "flex min-h-11 w-full cursor-pointer items-center justify-start gap-2.5 rounded-card border border-slate-200 bg-white px-3.5 text-right text-sm font-semibold text-slate-500 no-underline shadow-nav-button transition-all duration-200 hover:-translate-x-1 hover:border-primary/25 hover:bg-slate-50 max-[1200px]:px-3",
                    collapsed && "lg:min-h-11 lg:justify-center lg:gap-0 lg:px-0 lg:hover:translate-x-0",
                    isActive && "border-primary/40 bg-nav-active text-sidebar-accent-foreground shadow-nav-button-active",
                  )
                }
              >
                <span className="nav-icon text-inherit" aria-hidden="true">✏️</span>
                <span className={cn("nav-label text-inherit", collapsed && "lg:hidden")}>
                  طراح گزارش
                </span>
              </NavLink>
            )}
          </nav>
        )}
      </SidebarContent>

      {profile && !loading && (
        <SidebarFooter className="mt-auto shrink-0 border-t border-slate-200/80 bg-sidebar-gradient p-2 max-[480px]:p-1.5">
          <div
            className={cn(
              "flex items-center justify-center gap-1",
              collapsed && "lg:gap-px",
            )}
          >
            <button
              type="button"
              className={footerMicroButtonClass(collapsed)}
              onClick={handleLogout}
              title="خروج"
              aria-label="خروج"
            >
              <span aria-hidden="true">↩</span>
              <span className={cn(collapsed && "lg:hidden")}>خروج</span>
            </button>
            {(appVersion || buildMeta?.build_time) && (
              <button
                type="button"
                className={footerMicroButtonClass(collapsed)}
                onClick={() => setFooterModal("version")}
                title={appVersion ? `نسخه ${appVersion}` : "نسخه"}
                aria-label="نسخه"
              >
                <span className={cn(collapsed && "lg:hidden")}>
                  {appVersion ? `v${appVersion}` : "نسخه"}
                </span>
                <span className={cn("hidden", collapsed && "lg:inline")}>v</span>
              </button>
            )}
            {buildMeta?.commit && (
              <button
                type="button"
                className={cn(footerMicroButtonClass(collapsed), "font-mono")}
                onClick={() => setFooterModal("commit")}
                title={buildMeta.commit}
                aria-label="کامیت"
              >
                <span className={cn(collapsed && "lg:hidden")} dir="ltr">
                  {String(buildMeta.commit).slice(0, 7)}
                </span>
                <span className={cn("hidden", collapsed && "lg:inline")}>#</span>
              </button>
            )}
            <CustomizePopover
              collapsed={collapsed}
              className={footerMicroButtonClass(collapsed)}
            />
          </div>
          <AppDialog
            open={footerModal === "version"}
            onOpenChange={(open) => {
              if (!open) setFooterModal(null);
            }}
            title="نسخه"
            cancelLabel="بستن"
          >
            <dl className="space-y-2 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-slate-500">نسخه</dt>
                <dd className="font-bold" dir="ltr">
                  {appVersion ? `v${appVersion}` : "—"}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-slate-500">تاریخ ساخت</dt>
                <dd className="font-bold" dir="ltr">
                  {compactBuildDate(buildMeta?.build_time) || "—"}
                </dd>
              </div>
            </dl>
          </AppDialog>
          <AppDialog
            open={footerModal === "commit"}
            onOpenChange={(open) => {
              if (!open) setFooterModal(null);
            }}
            title="کامیت"
            cancelLabel="بستن"
          >
            <p className="break-all font-mono text-sm text-slate-800" dir="ltr">
              {buildMeta?.commit || "—"}
            </p>
            <p className="mt-2 text-xs text-slate-500">
              تاریخ ساخت: {compactBuildDate(buildMeta?.build_time) || "—"}
            </p>
          </AppDialog>
        </SidebarFooter>
      )}
      <SidebarRail />
    </Sidebar>
  );
}
